// 同意文言編集
import React, { useState, useEffect } from 'react';
import { render } from 'react-dom';
import axios from '../../../axios';
import { handleBrowserBack } from '../../../BrowserBack';
import { http_status_code_list, maintenance_type } from '../../../Const';
import Maintenance from '../../../molecules/Maintenance';
import LoadingModal from '../../../molecules/LoadingModal';
import StaffNavbar from '../../../molecules/StaffNavbar';
import ReactDatePicker from 'react-datepicker';
import ja from 'date-fns/locale/ja';
import PopupStaff from '../../../molecules/PopupStaff';
import ValidMessage from '../../../atoms/ValidMessage';
import Wysiwyg from '../../../molecules/Wysiwyg';
import { isBrowserIe } from '../../../BrowserJudgment';

interface Clause {
  id?: string;
  ja_name?: string;
  title?: string;
  text?: string;
  type_id?: string;
  type?: string;
  start_date?: string;
  end_date?: string;
}

interface ErrorText {
  title?: string;
  text?: string;
  start_date?: string;
}

const ClauseEdit = () => {
  const [clause, setClause] = useState<Clause>();
  const [error_text, setErrorText] = useState<ErrorText>();
  const [is_confirm, setIsConfirm] = useState<boolean>(false);
  const [is_popup, setIsPopup] = useState<boolean>(false);
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(true);
  const url_params = new URLSearchParams(document.location.search.substring(1));
  const clause_id = url_params.get('clause_id');

  // グローバルナビゲーション表示用
  const [role, setRole] = useState('');
  const [has_loan_aproval_system, setHasLoanAprovalSystem] = useState(false);
  // 組織区分コードの取得
  const node = document.getElementById('clause-edit');

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    // 表示する同意文言の取得
    getClause();
    // グローバルナビゲーション表示用
    setUserRole();
  }, []);

  // タイトル入力
  const handleChangeTitle = (value: string) => {
    setClause({ ...clause, title: value });
  };

  // 本文入力
  const handleChangeText = (value: string) => {
    // 入力内容がhtmlタグのみか判定し、trueの場合は入力保持内容は空とする
    if (value.replace(/<(.|\n)*?>/g, '').trim().length === 0) {
      setClause({ ...clause, text: '' });
    } else {
      setClause({ ...clause, text: value });
    }
  };

  // 開始日入力
  const handleChangeStartDate = (value: Date) => {
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();
    const start_date =
      year + '-' + month.toString().padStart(2, '0') + '-' + day.toString().padStart(2, '0');
    setClause({ ...clause, start_date: start_date });
  };

  // 同意文言を取得し表示する
  const getClause = async () => {
    try {
      const response = await axios.get(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/get_clause`,
        {
          params: {
            clause_id: clause_id,
          },
        }
      );
      if (response.data.result) {
        setClause(response.data.clause);
      } else {
        console.error(response.data.message);
        alert(response.data.message);
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 削除ボタン押下時
  const checkDeleteMailTemplate = async () => {
    try {
      setIsLoading(true);
      setErrorText({});
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/check_delete_clause`,
        {
          clause_id: clause_id,
        }
      );
      if (response.data.result) {
        setIsPopup(true);
      } else {
        console.error(response.data.message);
        setErrorText({
          start_date: response.data.message,
        });
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 削除確認ポップアップのOKボタン押下時
  const deleteClause = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/delete_clause`,
        {
          clause_id: clause_id,
        }
      );

      if (response.data.result) {
        // 編集完了画面に遷移
        location.replace(
          `${location.origin}${process.env.MIX_MOCK_URL}/maintenance/clause_complete`
        );
      } else {
        console.error(response.data.message);
        alert(response.data.message);
        setIsLoading(false);
        setIsPopup(false);
      }
    } catch (error) {
      console.error(error);
      location.replace(error.response.data.url);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
      setIsLoading(false);
    }
  };

  // プレビューボタン押下時
  const handlePreview = async () => {
    try {
      setIsLoading(true);

      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/preview_clause`,
        {
          ja_name: clause?.ja_name,
          clause_text: clause?.text,
        }
      );

      if (response.data.result) {
        // 新規タブでプレビュー画面を表示する
        window.open(
          `${location.origin}${process.env.MIX_MOCK_URL}/maintenance/clause_preview`,
          '_blank'
        );
      } else {
        console.error(response.data.message);
        alert(response.data.message);
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 登録ボタン押下時
  const handleRegister = async () => {
    try {
      setIsLoading(true);
      setErrorText({});
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/check_clause`,
        {
          id: clause_id,
          type: clause?.type_id,
          title: clause?.title,
          text: clause?.text,
          start_date: clause?.start_date,
          end_date: clause?.end_date,
        }
      );

      if (response.data.result) {
        setIsConfirm(true);
      } else {
        setErrorText({
          title: response.data.message.title,
          text: response.data.message.text,
          start_date: response.data.message.start_date,
        });
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 確認ボタン押下時
  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/register_clause`,
        {
          id: clause_id,
          type: clause?.type_id,
          title: clause?.title,
          text: clause?.text,
          start_date: clause?.start_date,
          end_date: clause?.end_date,
        }
      );

      if (response.data.result) {
        // 編集完了画面に遷移
        location.replace(
          `${location.origin}${process.env.MIX_MOCK_URL}/maintenance/clause_complete`
        );
      } else {
        alert('システムエラーが発生しました');
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      if (error.response.status === http_status_code_list.unauthorized) {
        // セッション切れの場合はログイン画面に遷移
        location.replace(error.response.data.url);
        return;
      }
      alert(error.response.data.message);
      setIsLoading(false);
    }
  };

  // ロールを設定する
  const setUserRole = () => {
    if (node === null) {
      // nodeが正しく取得できない場合は空文字とする
      setRole('');
      setHasLoanAprovalSystem(false);
      return;
    }

    // ロール名をセット
    setRole(node.dataset.role as string);

    // 融資稟議システム導入済みフラグをセット
    if ((node.dataset.has_loan_aproval_system as string) === '1') {
      setHasLoanAprovalSystem(true);
    } else {
      setHasLoanAprovalSystem(false);
    }
  };

  return (
    <>
      <StaffNavbar user_role={role} has_loan_aproval_system={has_loan_aproval_system} />
      <Maintenance active={maintenance_type.clauses}>
        <div className="page-title">
          同意文言編集
          {is_confirm && '(確認)'}
        </div>
        {typeof clause === 'object' ? (
          <>
            <table className="detail_table edit">
              <tbody>
                <tr>
                  <th>JA名</th>
                  <td>{clause.ja_name}</td>
                </tr>
                <tr>
                  <th>同意種類</th>
                  <td>{clause.type}</td>
                </tr>
                <tr>
                  <th>タイトル</th>
                  <td>
                    {is_confirm ? (
                      clause.title
                    ) : (
                      <>
                        <input
                          className={`form-control ${error_text?.title && 'textarea-error'}`}
                          type="text"
                          value={clause.title}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            handleChangeTitle(event.target.value)
                          }
                        />
                        <ValidMessage message={error_text?.title} />
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>本文</th>
                  <td>
                    {is_confirm ? (
                      <div className="small_text">
                        ※同意文言の表示はプレビュー画面を開いてご確認下さい
                      </div>
                    ) : isBrowserIe() ? (
                      <span className="ie_error">
                        ※同意文言の本文編集機能はIEではご利用頂けません。他のブラウザ(Edge・Chrome)をご利用下さい。
                      </span>
                    ) : (
                      <>
                        <Wysiwyg
                          class_name={`${error_text?.text && 'textarea-error'}`}
                          text={clause.text}
                          handleChange={handleChangeText}
                        />
                        <ValidMessage message={error_text?.text} />
                      </>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>開始日</th>
                  <td>
                    {is_confirm && clause.start_date ? (
                      clause.start_date
                    ) : (
                      <>
                        <ReactDatePicker
                          className={`form-control ${error_text?.start_date && 'textarea-error'}`}
                          selected={new Date(clause.start_date ?? '')}
                          onChange={handleChangeStartDate}
                          locale={ja}
                          dateFormat="yyyy-MM-dd"
                        />
                        <ValidMessage message={error_text?.start_date} />
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="footer-btn">
              {is_confirm ? (
                <>
                  <div
                    className="btn btn-mini btn-outline-success btn-text-color"
                    onClick={() => setIsConfirm(false)}
                  >
                    戻る
                  </div>
                  <div
                    className="btn btn-mini btn-outline-success btn-text-color"
                    onClick={handlePreview}
                  >
                    プレビュー
                  </div>
                  <div className="btn btn-mini btn-success" onClick={handleConfirm}>
                    確認
                  </div>
                </>
              ) : (
                <>
                  <a
                    className="btn btn-mini btn-outline-success btn-text-color"
                    href={`${location.origin}${process.env.MIX_MOCK_URL}/maintenance/clause_detail?clause_id=${clause_id}`}
                  >
                    キャンセル
                  </a>
                  <div className="btn btn-mini btn-danger" onClick={checkDeleteMailTemplate}>
                    削除
                  </div>
                  <a
                    className="btn btn-mini btn-outline-success btn-text-color"
                    onClick={handlePreview}
                  >
                    プレビュー
                  </a>
                  <div className="btn btn-mini btn-success" onClick={handleRegister}>
                    登録
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <></>
        )}
        <LoadingModal show={is_loading} />
        <PopupStaff
          toggleShow={is_popup}
          text={'この同意文言を削除します。よろしいですか？'}
          btn1_label={'OK'}
          btn1_css={'btn-danger'}
          btn1_func={deleteClause}
          btn2_label={'キャンセル'}
          btn2_css={'btn-outline-success btn-text-color'}
          btn2_func={() => {
            setIsPopup(false);
          }}
        />
      </Maintenance>
    </>
  );
};

if (document.getElementById('clause-edit')) {
  render(<ClauseEdit />, document.getElementById('clause-edit'));
}
